/**
 * Client-side validators — JS twins of the C# engine's per-rule validators.
 * Used to populate inline error messages reactively (Technique 6).
 *
 * Add new validator types here when extending support for new rule keys. Each one is
 * a small pure function.
 */

import { RuleSet } from './validation-rules.service';

export interface ClientValidationContext {
    dto: any;
    conditions: Record<string, any>;
    customValidators?: Record<string, (value: any, ctx: ClientValidationContext, rule: RuleSet) => boolean>;
}

function isEmpty(value: any): boolean {
    if (value == null) return true;
    if (typeof value === 'string') {
        const t = value.trim();
        return t === '' || t === '[]' || t === '{}';
    }
    if (Array.isArray(value)) return value.length === 0;
    if (typeof value === 'number') return false;
    if (typeof value === 'boolean') return false;
    return false;
}

function len(value: any): number {
    if (value == null) return 0;
    if (typeof value === 'string') return value.length;
    if (Array.isArray(value)) return value.length;
    return String(value).length;
}

function plainText(value: any): string {
    if (value == null) return '';
    const s = String(value);
    if (/<[^>]+>/.test(s)) {
        const div = document.createElement('div');
        div.innerHTML = s;
        return (div.innerText || div.textContent || '').replace(/\s+/g, ' ').trim();
    }
    return s;
}

function fallbackMessage(rule: RuleSet, field: string, kind: string): string {
    if (rule.Message) return String(rule.Message);
    if (rule.ValidationMessageId) return String(rule.ValidationMessageId);
    switch (kind) {
        case 'required':   return `${field} is required.`;
        case 'maxLength':  return `${field} cannot exceed ${rule.MaxLength} characters.`;
        case 'minLength':  return `${field} must be at least ${rule.MinLength} characters.`;
        case 'min':        return `${field} must be at least ${rule.Min}.`;
        case 'max':        return `${field} must not exceed ${rule.Max}.`;
        case 'pattern':    return `${field} format is invalid.`;
        case 'range':      return `${field} must be one of the allowed values.`;
        case 'minCount':   return `${field} must have at least ${rule.MinCount} entries.`;
        case 'minDate':    return `${field} must be on or after ${rule.MinDate}.`;
        case 'maxDate':    return `${field} must be on or before ${rule.MaxDate}.`;
        case 'beforeField':return `${field} must be before ${rule.BeforeField}.`;
        case 'afterField': return `${field} must be after ${rule.AfterField}.`;
        case 'sameDayAs':  return `${field} must be the same day as ${(rule as any).SameDayAs}.`;
        case 'greaterThanOrEqual': return `${field} must be on or after ${rule.GreaterThanOrEqual}.`;
        case 'lessThanOrEqual':    return `${field} must be on or before ${rule.LessThanOrEqual}.`;
        default:           return `${field} is invalid.`;
    }
}

export function runRule(value: any, rule: RuleSet, field: string, ctx: ClientValidationContext): string | null {
    if (rule.Required === true && isEmpty(value)) {
        return fallbackMessage(rule, field, 'required');
    }

    if (typeof value === 'number' && value === 0 && rule.Min != null && Number(rule.Min) > 0) {
        return fallbackMessage(rule, field, 'min');
    }

    const txt = typeof value === 'string' ? plainText(value) : value;

    if (rule.MaxLength != null && len(txt) > Number(rule.MaxLength)) {
        return fallbackMessage(rule, field, 'maxLength');
    }
    if (rule.MinLength != null && !isEmpty(txt) && len(txt) < Number(rule.MinLength)) {
        return fallbackMessage(rule, field, 'minLength');
    }
    if (rule.Min != null && value != null && value !== '' && !isNaN(Number(value)) && Number(value) < Number(rule.Min)) {
        return fallbackMessage(rule, field, 'min');
    }
    if (rule.Max != null && value != null && value !== '' && !isNaN(Number(value)) && Number(value) > Number(rule.Max)) {
        return fallbackMessage(rule, field, 'max');
    }
    if (rule.Regex && !isEmpty(value)) {
        try {
            if (!new RegExp(String(rule.Regex)).test(String(value))) {
                return fallbackMessage(rule, field, 'pattern');
            }
        } catch { /* invalid regex in DB — skip rather than crash */ }
    }
    if (rule.Range) {
        const allowed = String(rule.Range).split(',').map(s => s.trim().toLowerCase());
        if (!isEmpty(value) && !allowed.includes(String(value).toLowerCase())) {
            return fallbackMessage(rule, field, 'range');
        }
    }

    if (rule.MinCount != null) {
        const list = Array.isArray(value) ? value : [];
        let filtered = list;
        if (rule.FilterField && rule.FilterValue != null) {
            filtered = list.filter((item: any) => {
                if (item == null) return false;
                const v = item[rule.FilterField as string];
                return String(v) === String(rule.FilterValue);
            });
        }
        if (filtered.length < Number(rule.MinCount)) {
            return fallbackMessage(rule, field, 'minCount');
        }
    }

    if (rule.MinDate && value) {
        const raw: any = value;
        const d: Date = raw instanceof Date
            ? raw
            : (typeof raw?.toJSDate === 'function' ? raw.toJSDate() : new Date(raw));
        const ref = String(rule.MinDate).toLowerCase() === 'today' ? new Date() : new Date(String(rule.MinDate));
        if (!isNaN(d.getTime()) && !isNaN(ref.getTime())) {
            const dOnly = new Date(d.getFullYear(), d.getMonth(), d.getDate());
            const rOnly = new Date(ref.getFullYear(), ref.getMonth(), ref.getDate());
            if (dOnly < rOnly) {
                return fallbackMessage(rule, field, 'minDate');
            }
        }
    }
    if (rule.MaxDate && value) {
        const raw: any = value;
        const d: Date = raw instanceof Date
            ? raw
            : (typeof raw?.toJSDate === 'function' ? raw.toJSDate() : new Date(raw));
        const ref = String(rule.MaxDate).toLowerCase() === 'today' ? new Date() : new Date(String(rule.MaxDate));
        if (!isNaN(d.getTime()) && !isNaN(ref.getTime())) {
            const dOnly = new Date(d.getFullYear(), d.getMonth(), d.getDate());
            const rOnly = new Date(ref.getFullYear(), ref.getMonth(), ref.getDate());
            if (dOnly > rOnly) {
                return fallbackMessage(rule, field, 'maxDate');
            }
        }
    }

    if ((rule as any).MaxDateDaysFromNow != null && value) {
        const raw: any = value;
        const d: Date = raw instanceof Date
            ? raw
            : (typeof raw?.toJSDate === 'function' ? raw.toJSDate() : new Date(raw));
        if (!isNaN(d.getTime())) {
            const today = new Date();
            const limit = new Date(today.getFullYear(), today.getMonth(), today.getDate() + Number((rule as any).MaxDateDaysFromNow));
            const dOnly = new Date(d.getFullYear(), d.getMonth(), d.getDate());
            if (dOnly > limit) return fallbackMessage(rule, field, 'maxDate');
        }
    }

    if ((rule as any).MinDateDaysFromNow != null && value) {
        const raw: any = value;
        const d: Date = raw instanceof Date
            ? raw
            : (typeof raw?.toJSDate === 'function' ? raw.toJSDate() : new Date(raw));
        if (!isNaN(d.getTime())) {
            const today = new Date();
            const limit = new Date(today.getFullYear(), today.getMonth(), today.getDate() + Number((rule as any).MinDateDaysFromNow));
            const dOnly = new Date(d.getFullYear(), d.getMonth(), d.getDate());
            if (dOnly < limit) return fallbackMessage(rule, field, 'minDate');
        }
    }

    if (rule.BeforeField && value) {
        const other = ctx.dto?.[String(rule.BeforeField)];
        if (other) {
            const a = value instanceof Date ? value : new Date(value);
            const b = other instanceof Date ? other : new Date(other);
            if (!isNaN(a.getTime()) && !isNaN(b.getTime()) && a >= b) {
                return fallbackMessage(rule, field, 'beforeField');
            }
        }
    }
    if (rule.AfterField && value) {
        const other = ctx.dto?.[String(rule.AfterField)];
        if (other) {
            const a = value instanceof Date ? value : new Date(value);
            const b = other instanceof Date ? other : new Date(other);
            if (!isNaN(a.getTime()) && !isNaN(b.getTime()) && a <= b) {
                return fallbackMessage(rule, field, 'afterField');
            }
        }
    }
    if ((rule as any).SameDayAs && value) {
        const refKey = String((rule as any).SameDayAs);
        const other = ctx.dto?.[refKey] ?? ctx.dto?.[refKey.charAt(0).toLowerCase() + refKey.slice(1)];
        if (other) {
            const toDate = (v: any): Date => v instanceof Date ? v : (typeof v?.toJSDate === 'function' ? v.toJSDate() : new Date(v));
            const a = toDate(value);
            const b = toDate(other);
            if (!isNaN(a.getTime()) && !isNaN(b.getTime()) && a.toDateString() !== b.toDateString()) {
                return fallbackMessage(rule, field, 'sameDayAs');
            }
        }
    }
    if (rule.GreaterThanOrEqual && value) {
        const refKey = String(rule.GreaterThanOrEqual);
        const other = ctx.dto?.[refKey] ?? ctx.dto?.[refKey.charAt(0).toLowerCase() + refKey.slice(1)];
        if (other) {
            const a = value instanceof Date ? value : new Date(value);
            const b = other instanceof Date ? other : new Date(other);
            if (!isNaN(a.getTime()) && !isNaN(b.getTime()) && a < b) {
                return fallbackMessage(rule, field, 'greaterThanOrEqual');
            }
        }
    }
    if (rule.LessThanOrEqual && value) {
        const refKey = String(rule.LessThanOrEqual);
        const other = ctx.dto?.[refKey] ?? ctx.dto?.[refKey.charAt(0).toLowerCase() + refKey.slice(1)];
        if (other) {
            const a = value instanceof Date ? value : new Date(value);
            const b = other instanceof Date ? other : new Date(other);
            if (!isNaN(a.getTime()) && !isNaN(b.getTime()) && a > b) {
                return fallbackMessage(rule, field, 'lessThanOrEqual');
            }
        }
    }

    if (rule.ValidationFunction && ctx.customValidators) {
        const fn = ctx.customValidators[String(rule.ValidationFunction)];
        if (fn && !fn(value, ctx, rule)) {
            return rule.Message ? String(rule.Message) : fallbackMessage(rule, field, 'custom');
        }
    }

    return null;
}
